import React, { useEffect, useState } from 'react';
import { useVersionCheck } from '../hooks/useVersionCheck';

export function UpdateBanner() {
    const { updateAvailable, applyUpdate } = useVersionCheck();
    const [visible, setVisible] = useState(false);
    const [applying, setApplying] = useState(false);
    const [progress, setProgress] = useState(0);

    // Animate in when update becomes available
    useEffect(() => {
        if (updateAvailable) {
            setTimeout(() => setVisible(true), 100);
        }
    }, [updateAvailable]);

    const handleUpdate = () => {
        setApplying(true);
        // Animate progress bar then apply
        let p = 0;
        const tick = setInterval(() => {
            p += Math.random() * 18 + 5;
            setProgress(Math.min(p, 90));
            if (p >= 90) {
                clearInterval(tick);
                setProgress(99);
                setTimeout(() => applyUpdate(), 400);
            }
        }, 120);
    };

    if (!updateAvailable) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                zIndex: 99999,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                opacity: visible ? 1 : 0,
                transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                maxWidth: '360px',
                width: 'calc(100vw - 48px)',
            }}
        >
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '16px',
                boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
                overflow: 'hidden',
                fontFamily: "'Inter', system-ui, sans-serif",
            }}>
                {/* Glow accent */}
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)',
                    borderRadius: '16px 16px 0 0',
                }} />

                <div style={{ padding: '18px 20px 16px', position: 'relative' }}>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
                        {/* Pulse icon */}
                        <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                            <div style={{
                                width: '36px', height: '36px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 0 16px rgba(99,102,241,0.5)',
                            }}>
                                {/* Rocket SVG */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                                </svg>
                            </div>
                            {/* Pulse ring */}
                            <div style={{
                                position: 'absolute', inset: '-4px',
                                borderRadius: '14px',
                                border: '2px solid rgba(99,102,241,0.4)',
                                animation: 'pulse-ring 2s ease-out infinite',
                            }} />
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px',
                            }}>
                                <span style={{
                                    fontSize: '14px', fontWeight: '700',
                                    color: '#f1f5f9', letterSpacing: '-0.01em',
                                }}>
                                    Update Available
                                </span>
                                <span style={{
                                    fontSize: '10px', fontWeight: '600',
                                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    color: 'white', padding: '2px 7px', borderRadius: '100px',
                                    letterSpacing: '0.03em', textTransform: 'uppercase',
                                }}>
                                    New
                                </span>
                            </div>
                            <p style={{
                                fontSize: '12px', color: '#94a3b8',
                                margin: 0, lineHeight: '1.5',
                            }}>
                                A new version is ready. Update now to get the latest features and fixes.
                            </p>
                        </div>
                    </div>

                    {/* Progress bar (shown during update) */}
                    {applying && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                marginBottom: '5px',
                            }}>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>Clearing cache...</span>
                                <span style={{ fontSize: '11px', color: '#6366f1', fontWeight: '600' }}>{Math.round(progress)}%</span>
                            </div>
                            <div style={{
                                height: '4px', borderRadius: '4px',
                                background: 'rgba(99,102,241,0.15)',
                                overflow: 'hidden',
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${progress}%`,
                                    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                    borderRadius: '4px',
                                    transition: 'width 0.15s ease',
                                }} />
                            </div>
                        </div>
                    )}

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={handleUpdate}
                            disabled={applying}
                            style={{
                                flex: 1,
                                padding: '9px 16px',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: applying ? 'not-allowed' : 'pointer',
                                background: applying
                                    ? 'rgba(99,102,241,0.4)'
                                    : 'linear-gradient(135deg, #6366f1, #7c3aed)',
                                color: 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                letterSpacing: '-0.01em',
                                transition: 'all 0.2s',
                                boxShadow: applying ? 'none' : '0 4px 12px rgba(99,102,241,0.4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                            }}
                            onMouseEnter={e => {
                                if (!applying) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                            }}
                        >
                            {applying ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="1 4 1 10 7 10" />
                                        <path d="M3.51 15a9 9 0 1 0 .49-3" />
                                    </svg>
                                    Update Now
                                </>
                            )}
                        </button>

                        {!applying && (
                            <button
                                onClick={() => setVisible(false)}
                                style={{
                                    padding: '9px 12px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    cursor: 'pointer',
                                    background: 'rgba(255,255,255,0.04)',
                                    color: '#64748b',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
                                    (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                                    (e.currentTarget as HTMLButtonElement).style.color = '#64748b';
                                }}
                            >
                                Later
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Inline keyframe styles */}
            <style>{`
                @keyframes pulse-ring {
                    0% { transform: scale(1); opacity: 0.6; }
                    100% { transform: scale(1.35); opacity: 0; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
